import React from "react";
import Blockies from "react-blockies";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  Snackbar,
  useTheme,
} from "@mui/material";
import { ImEye, ImEyeBlocked } from "react-icons/im";
import { MdPersonAddAlt1, MdPersonRemoveAlt1 } from "react-icons/md";
import { TbUsersPlus } from "react-icons/tb";
import { useAppDispatch } from "../../../hooks";
import useAlert from "../../../hooks/useAlert";
import {
  useConnectionCheck,
  ConnectionState,
} from "../../../hooks/useConnectionCheck";
import useBasicProfile from "../../../hooks/useBasicProfile";
import AddFriendModal from "../../FriendAction/AddFriendModal";
import BlockUserModal from "../../FriendAction/BlockUserModal";
import CancelBlockUserModal from "../../FriendAction/CancelBlockUserModal";
import DeleteUserModal from "../../FriendAction/DeleteUserModal";
import AssetList from "../AssetList";
import CommonCard from "../CommonCard";
import "./index.css";
import { useWeb3Auth } from "../../../hooks/useWeb3Auth";
import SparklingAvatar from "../../SparklingAvatar";
import AIAvatarPage from "../AIAvatarPage";

type MenuDict = {
  [k in ConnectionState]: Array<MenuEnum>;
};
enum MenuEnum {
  add_friend,
  block,
  delete_friend,
  cancel_block,
}

interface IProps {
  address: string;
  uuid?: string;
  callback: any;
}
const NormalPage: React.FC<IProps> = ({ address, uuid, callback }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const {
    address: currentAddress,
    isProticoAuthenticated,
    proticoAuthenticate,
  } = useWeb3Auth();
  const isMe = currentAddress === address;
  const [isOpen, alertContent, isError, setOptionalAlertContent] = useAlert();
  const { isPending, basicProfile } = useBasicProfile(address);
  const { isConnectionStateFetching, connectionCheck } =
    useConnectionCheck(address);
  const [showAIAvatar, setShowAIAvatar] = React.useState(false);
  const isLevel1 = basicProfile?.aiavatar?.level === "level1";
  const [anchorElMore, setAnchorElMore] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElFriend, setAnchorElFriend] =
    React.useState<null | HTMLElement>(null);
  const [anchorElBlock, setAnchorElBlock] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElDelete, setAnchorElDelete] =
    React.useState<null | HTMLElement>(null);
  const [anchorElCancelBlock, setAnchorElCancelBlock] =
    React.useState<null | HTMLElement>(null);
  const handleOpenFriend = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFriend(event.currentTarget);
  };
  const handleCloseFriend = () => {
    setAnchorElFriend(null);
  };
  const handleOpenBlock = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElBlock(event.currentTarget);
  };
  const handleCloseBlock = () => {
    setAnchorElBlock(null);
  };
  const handleOpenDelete = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElDelete(event.currentTarget);
  };
  const handleCloseDelete = () => {
    setAnchorElDelete(null);
  };
  const handleOpenCancelBlock = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElCancelBlock(event.currentTarget);
  };
  const handleCloseCancelBlock = () => {
    setAnchorElCancelBlock(null);
  };

  const handleMore = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
  };
  const handleCloseMore = () => {
    setAnchorElMore(null);
  };

  const menuDict: MenuDict = {
    [ConnectionState.NONE]: [MenuEnum.add_friend, MenuEnum.block],
    [ConnectionState.FRIEND]: [MenuEnum.delete_friend, MenuEnum.block],
    [ConnectionState.WAITING]: [MenuEnum.delete_friend, MenuEnum.block],
    [ConnectionState.BLOCK]: [MenuEnum.cancel_block],
    [ConnectionState.REQUESTED]: [MenuEnum.add_friend, MenuEnum.block],
  };
  var JSXDict = {
    [MenuEnum.add_friend]: (
      <MenuItem
        key="add_friend"
        disabled={
          !isConnectionStateFetching &&
          connectionCheck.connectionState === ConnectionState.FRIEND
        }
        style={{ color: "#e5e5e5", fontSize: "12px", fontWeight: "600" }}
        onClick={(e) => {
          handleOpenFriend(e);
          handleCloseMore();
        }}
      >
        <MdPersonAddAlt1 size={18} style={{ marginRight: "5px" }} />
        {t("FlashChat_Profile_Add_Friend")}
      </MenuItem>
    ),
    [MenuEnum.block]: (
      <MenuItem
        key="block"
        style={{ color: "#e5e5e5", fontSize: "12px", fontWeight: "600" }}
        onClick={(e) => {
          handleOpenBlock(e);
          handleCloseMore();
        }}
      >
        <ImEyeBlocked size={14} style={{ margin: "0 7px 0 1px" }} />
        {t("FlashChat_Profile_Block_User")}
      </MenuItem>
    ),
    [MenuEnum.delete_friend]: (
      <MenuItem
        key="delete_friend"
        style={{ color: "#e5e5e5", fontSize: "12px", fontWeight: "600" }}
        onClick={(e) => {
          handleOpenDelete(e);
          handleCloseMore();
        }}
      >
        <MdPersonRemoveAlt1 size={18} style={{ marginRight: "5px" }} />
        {t("FlashChat_Profile_Delete_User")}
      </MenuItem>
    ),
    [MenuEnum.cancel_block]: (
      <MenuItem
        key="cancel_block"
        style={{ color: "#e5e5e5", fontSize: "12px", fontWeight: "600" }}
        onClick={(e) => {
          handleOpenCancelBlock(e);
          handleCloseMore();
        }}
      >
        <ImEye size={14} style={{ margin: "0 7px 0 1px" }} />
        {t("FlashChat_Profile_Block_User_Cancel")}
      </MenuItem>
    ),
  };
  const getMenuItem = () => {
    var menuList: Array<JSX.Element> = [];
    if (isConnectionStateFetching || connectionCheck === undefined)
      return menuList;
    menuDict[connectionCheck.connectionState].forEach((el) =>
      menuList.push(JSXDict[el])
    );
    return menuList;
  };

  const handleClickProticoAuthenticate = (e: any) => {
    e.preventDefault();
    proticoAuthenticate();
  };

  return (
    <div className="normalPage-container">
      {isPending || !basicProfile ? (
        <CircularProgress style={{ color: theme.brandColor }} size={20} />
      ) : (
        <>
          <Snackbar
            open={isOpen}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              variant="filled"
              severity={isError ? "error" : "success"}
              color={isError ? "error" : "info"}
            >
              <strong>{alertContent}</strong>
            </Alert>
          </Snackbar>

          <AddFriendModal
            open={Boolean(anchorElFriend)}
            onClose={handleCloseFriend}
            address={address}
          />
          <BlockUserModal
            open={Boolean(anchorElBlock)}
            onClosePage={handleCloseBlock}
            address={address}
            callback={callback}
          />
          <CancelBlockUserModal
            open={Boolean(anchorElCancelBlock)}
            onClosePage={handleCloseCancelBlock}
            address={address}
            callback={callback}
          />
          <DeleteUserModal
            open={Boolean(anchorElDelete)}
            onClosePage={handleCloseDelete}
            address={address}
            callback={callback}
          />
          {showAIAvatar && (
            <AIAvatarPage
              onClose={() => setShowAIAvatar(false)}
              aiavatar={basicProfile.aiavatar}
              isEditable={false}
            />
          )}

          <div className="normalPage-user-info">
            <div className="normalPage-user-info-upper-block">
              {basicProfile.avatar_url ? (
                isLevel1 ? (
                  <div
                    className="normalPage-aiavatar-container"
                    onClick={() => {
                      setShowAIAvatar(true);
                    }}
                  >
                    <SparklingAvatar
                      src={basicProfile?.avatar_url}
                      width="5rem"
                      height="5rem"
                    />
                  </div>
                ) : (
                  <Avatar
                    alt="PFP"
                    src={basicProfile?.avatar_url}
                    sx={{
                      width: "5rem",
                      height: "5rem",
                    }}
                  />
                )
              ) : (
                <Blockies
                  seed={address ? address : uuid}
                  size={10}
                  scale={3}
                  color="#ddd"
                  className="identicon avator-big"
                />
              )}
              <CommonCard
                handleSetAlert={setOptionalAlertContent}
                address={address}
                basicProfile={basicProfile}
              />
            </div>
            <div className="normalPage-user-info-desc">
              {basicProfile.description}
            </div>
          </div>
          {!isMe && isProticoAuthenticated && address !== "" && (
            <>
              <div className="normalPage-editButton-container">
                <div
                  className="normalPage-editButton"
                  aria-label="more"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMore}
                >
                  <TbUsersPlus className="normalPage-editButtonIcon" />
                  <span className="normalPage-editButtonText">
                    {connectionCheck.connectionState === ConnectionState.NONE
                      ? t("FlashChat_Profile_Add_Friend_Btn")
                      : t("FlashChat_Profile_Manage_Friend_Btn")}
                  </span>
                </div>
              </div>
              <Menu
                sx={{ mt: "40px" }}
                id="menu-appbar"
                anchorEl={anchorElMore}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                open={Boolean(anchorElMore)}
                onClose={handleCloseMore}
              >
                {getMenuItem()}
              </Menu>
            </>
          )}
          {isMe && !isProticoAuthenticated && (
            <div className="normalPage-editButton-container">
              <div
                className="normalPage-editButton"
                aria-label="more"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleClickProticoAuthenticate}
              >
                {t("FlashChat_Profile_Auth_Btn")}
              </div>
            </div>
          )}

          <AssetList address={address} />
        </>
      )}
    </div>
  );
};
export default NormalPage;
